import { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { CANCEL_INBOUND_MEMO_LIMIT, type CancelInboundLineRequest } from '@/infrastructure/FulfillmentAdminClient';
import {
  CANCEL_INBOUND_MEMO_PLACEHOLDER,
  cancelInboundShelfRole,
} from '@/pages/fulfillment/components/cancelInboundShelf';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';

interface CancelInboundLineModalProps {
  readonly sku: string;
  readonly lineNo: number;
  /** How many units the arrival booked; the restock defaults to all of them and may not exceed them. */
  readonly arrivedQty: number;
  readonly busy: boolean;
  readonly onHide: () => void;
  readonly onConfirm: (request: CancelInboundLineRequest) => void;
}

/**
 * Taking an arrival back on the order detail screen. No restock is the default, as on View Orders (SSD-7674):
 * keeping the units is the stated decision — the same three controls the counting desk offers, so one physical
 * reversal is never recorded two different ways.
 */
export function CancelInboundLineModal({
  sku,
  lineNo,
  arrivedQty,
  busy,
  onHide,
  onConfirm,
}: CancelInboundLineModalProps) {
  const [restock, setRestock] = useState(false);
  const [restockQty, setRestockQty] = useState(String(arrivedQty));
  const [locationCode, setLocationCode] = useState('');
  const [memo, setMemo] = useState('');

  const qty = Number(restockQty);
  // Zero is what declining already says, and more than arrived never physically happened.
  const qtyValid = Number.isInteger(qty) && qty >= 1 && qty <= arrivedQty;
  const trimmed = locationCode.trim();
  const trimmedMemo = memo.trim();
  const writtenOff = restock && qtyValid ? arrivedQty - qty : 0;

  return (
    <Modal {...ULTIMATE_MODAL} show onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Cancel Inbound — {sku} (line {lineNo})
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">1. Restock this inventory?</Form.Label>
          <Form.Check
            type="radio"
            id="cancel-inbound-restock-yes"
            name="cancel-inbound-restock"
            label="Yes — restock"
            checked={restock}
            onChange={() => setRestock(true)}
          />
          <Form.Check
            type="radio"
            id="cancel-inbound-restock-no"
            name="cancel-inbound-restock"
            label="No"
            checked={!restock}
            onChange={() => setRestock(false)}
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="cancel-inbound-qty">
          <Form.Label className="fw-semibold">2. Restock Qty</Form.Label>
          <Form.Control
            type="number"
            aria-label="Restock Qty"
            min={1}
            max={arrivedQty}
            value={restockQty}
            disabled={!restock}
            onChange={(event) => setRestockQty(event.target.value)}
          />
          <Form.Text muted>
            Default = the {arrivedQty} unit(s) that arrived · a smaller figure writes the difference off
          </Form.Text>
        </Form.Group>
        <Form.Group className="mb-3" controlId="cancel-inbound-location">
          <Form.Label className="fw-semibold">3. Location (optional)</Form.Label>
          <Form.Control
            aria-label="Location"
            value={locationCode}
            onChange={(event) => setLocationCode(event.target.value)}
          />
          <Form.Text muted>
            {cancelInboundShelfRole(restock, writtenOff)} · leave blank to use the shelf the reservation names
          </Form.Text>
        </Form.Group>
        <Form.Group controlId="cancel-inbound-memo">
          <Form.Label className="fw-semibold">4. Memo (Optional)</Form.Label>
          <Form.Control
            as="textarea"
            aria-label="Memo (Optional)"
            rows={2}
            maxLength={CANCEL_INBOUND_MEMO_LIMIT}
            placeholder={CANCEL_INBOUND_MEMO_PLACEHOLDER}
            value={memo}
            onChange={(event) => setMemo(event.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Keep the inbound
        </Button>
        <Button
          variant="danger"
          disabled={busy || (restock && !qtyValid)}
          onClick={() =>
            onConfirm({
              restock,
              ...(restock && qty < arrivedQty ? { restockQty: qty } : {}),
              ...(trimmed ? { locationCode: trimmed } : {}),
              ...(trimmedMemo ? { memo: trimmedMemo } : {}),
            })
          }
        >
          Cancel Inbound
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
